import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Rss, ExternalLink, Loader2, PlayCircle, RefreshCw,
  CheckCircle, AlertCircle, XCircle, Trash2, Edit3, Save,
  Sparkles, Clock, FileText, Globe, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import SourceSettingsTab from '../components/feeds/SourceSettingsTab';
import SourceItemsTab from '../components/feeds/SourceItemsTab';
import SourceSubscriptionsTab from '../components/feeds/SourceSubscriptionsTab';
import SourceFetchRunsTab from '../components/feeds/SourceFetchRunsTab';
import SourceOverviewTab from '../components/feeds/SourceOverviewTab';
import SourceAutopilotCard from '../components/feeds/SourceAutopilotCard';

const fmtUK = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    timeZone: 'Europe/London',
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

const HEALTH_BADGES = {
  healthy:  { colour: 'bg-emerald-50 text-emerald-700 border-emerald-200',  icon: CheckCircle, label: 'Healthy' },
  warning:  { colour: 'bg-amber-50 text-amber-700 border-amber-200',        icon: AlertCircle, label: 'Warning' },
  degraded: { colour: 'bg-orange-50 text-orange-700 border-orange-200',     icon: AlertCircle, label: 'Degraded' },
  failed:   { colour: 'bg-red-50 text-red-600 border-red-200',              icon: XCircle,     label: 'Failed' },
  blocked:  { colour: 'bg-rose-50 text-rose-600 border-rose-200',           icon: XCircle,     label: 'Blocked' },
  unknown:  { colour: 'bg-slate-100 text-slate-500 border-slate-300',       icon: AlertCircle, label: 'Unknown' },
};


function HealthBadge({ status }) {
  const cfg = HEALTH_BADGES[status] || HEALTH_BADGES.unknown;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border ${cfg.colour}`}>
      <Icon size={12} />
      {cfg.label}
    </span>
  );
}


export default function FeedsSourceDetailPage() {
  const { sourceId } = useParams();
  const { user }     = useAuth();
  const navigate     = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const isNew = sourceId === 'new';
  const activeTab = searchParams.get('tab') || (isNew ? 'settings' : 'overview');

  const [source, setSource]         = useState(null);
  const [loading, setLoading]       = useState(!isNew);
  const [actionBusy, setActionBusy] = useState(null); // 'test' | 'fetch' | null

  useEffect(() => {
    if (!isNew) loadSource();
  }, [sourceId]);

  const loadSource = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/feeds/sources/${sourceId}`);
      setSource(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load source');
    } finally {
      setLoading(false);
    }
  };
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/feeds/sources');
  };

  const handleTabChange = (tab) => {
    setSearchParams({ tab }, { replace: true });
  };

  const handleTest = async () => {
    setActionBusy('test');
    try {
      const { data } = await api.post(`/feeds/sources/${sourceId}/test`);
      if (data.status === 'failed') {
        toast.error(`Test failed: ${data.error_message}`);
      } else {
        toast.success(`Test fetch returned ${data.items_found} items (no items saved)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
    } finally {
      setActionBusy(null);
    }
  };

  const handleFetch = async () => {
    setActionBusy('fetch');
    try {
      const { data } = await api.post(`/feeds/sources/${sourceId}/fetch`);
      if (data.status === 'failed') {
        toast.error(`Fetch failed: ${data.error_message}`);
      } else {
        toast.success(`Fetched ${data.items_found} items (${data.new_items_created} new)`);
        loadSource();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fetch failed');
    } finally {
      setActionBusy(null);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${source.source_name}"?\n\nThis removes all items, fetch logs, and subscriptions for this source.`)) return;
    try {
      await api.delete(`/feeds/sources/${sourceId}`);
      toast.success('Source deleted');
      navigate('/feeds/sources');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  if (user?.role !== 'admin' && user?.role !== 'editor') {
    return <div className="text-center py-16"><p className="text-slate-400">You do not have access to EventFlow.</p></div>;
  }

  // New source form
  if (isNew) {
    return (
      <div>
        <button
          onClick={goBack}
          className="flex items-center gap-2 text-slate-400 hover:text-slate-700 text-sm mb-4 transition-colors"
        >
          <ArrowLeft size={14} /> Back to Sources
        </button>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Add Feed Source</h1>
          <p className="text-slate-500 text-sm mt-1">
            Create a network-level source. After creation you can subscribe publications to it.
          </p>
        </div>

        <SourceSettingsTab
          source={null}
          onSaved={(created) => navigate(`/feeds/sources/${created.id}?tab=overview`)}
          onCancel={goBack}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
        <Loader2 size={16} className="animate-spin" /> Loading...
      </div>
    );
  }

  if (!source) {
    return <div className="text-center py-16 text-slate-400 text-sm">Source not found.</div>;
  }

  const tabs = [
    { id: 'overview',      label: 'Overview',      icon: Rss },
    { id: 'items',         label: 'Feed',          icon: FileText, count: source.total_items },
    { id: 'subscriptions', label: 'Subscriptions', icon: Globe,    count: source.subscription_count },
    { id: 'runs',          label: 'Fetch Runs',    icon: Clock },
    { id: 'settings',      label: 'Settings',      icon: Edit3 },
  ];

  return (
    <div>
      <button
        onClick={goBack}
        className="flex items-center gap-2 text-slate-400 hover:text-slate-700 text-sm mb-4 transition-colors"
      >
        <ArrowLeft size={14} /> Back to Sources
      </button>

      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5 shadow-sm">
        <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <Rss size={16} className="text-slate-400" />
              <h1 className="text-xl font-bold text-slate-900">{source.source_name}</h1>
              <HealthBadge status={source.health_status} />
              {!source.is_active && (
                <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-slate-100 text-slate-500 rounded border border-slate-200 font-semibold">
                  Inactive
                </span>
              )}
            </div>
            <a
              href={source.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-blue-600 font-mono"
            >
              {source.source_url} <ExternalLink size={11} />
            </a>
            <div className="flex items-center gap-4 mt-3 text-xs text-slate-400 flex-wrap">
              {source.primary_organisation_name && <span>Org: {source.primary_organisation_name}</span>}
              <span>Parser: {source.parser_type}</span>
              <span>Every {source.fetch_frequency_minutes} min</span>
              {source.last_fetched_at && (
                <span>Last fetched: {fmtUK(source.last_fetched_at)}</span>
              )}
            </div>
            {source.last_error_message && source.health_status !== 'healthy' && (
              <p className="text-xs text-red-500 mt-2 italic">{source.last_error_message}</p>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={handleTest}
              disabled={actionBusy !== null}
              className="flex items-center gap-1.5 px-3 py-1.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded text-xs font-medium border border-slate-200 transition-colors disabled:opacity-50"
            >
              {actionBusy === 'test' ? <Loader2 size={13} className="animate-spin" /> : <PlayCircle size={13} />}
              Test
            </button>
            <button
              onClick={handleFetch}
              disabled={actionBusy !== null || !source.is_active}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded text-xs font-medium border border-emerald-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {actionBusy === 'fetch' ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
              Fetch now
            </button>
            {user?.role === 'admin' && (
              <button
                onClick={handleDelete}
                className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                title="Delete source"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-slate-200">
          <Stat label="Feed" value={source.total_items || 0} />
          <Stat label="Feed (last 7d)" value={source.items_last_7_days || 0} />
          <Stat label="Active subs" value={source.active_subscription_count || 0} highlight={source.active_subscription_count > 0} />
          <Stat label="Failed runs (7d)" value={source.failed_runs_last_7_days || 0} warning={source.failed_runs_last_7_days > 0} />
        </div>
      </div>

      <SourceAutopilotCard source={source} onChange={loadSource} />

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl border border-slate-200 mb-6 w-fit overflow-x-auto">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => handleTabChange(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === t.id
                  ? 'bg-white text-blue-600 border border-slate-200 shadow-sm'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              <Icon size={14} />
              {t.label}
              {t.count > 0 && (
                <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[20px] text-center ${
                  activeTab === t.id ? 'bg-blue-50 text-blue-600' : 'bg-slate-200 text-slate-600'
                }`}>
                  {t.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {activeTab === 'overview'      && <SourceOverviewTab source={source} onRefresh={loadSource} />}
      {activeTab === 'items'         && <SourceItemsTab sourceId={sourceId} />}
      {activeTab === 'subscriptions' && <SourceSubscriptionsTab sourceId={sourceId} onChange={loadSource} />}
      {activeTab === 'runs'          && <SourceFetchRunsTab sourceId={sourceId} />}
      {activeTab === 'settings'      && (
        <SourceSettingsTab
          source={source}
          onSaved={() => { loadSource(); toast.success('Settings updated'); }}
          onCancel={() => handleTabChange('overview')}
        />
      )}
    </div>
  );
}


function Stat({ label, value, highlight, warning }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
      <p className={`text-xl font-bold mt-0.5 ${
        warning ? 'text-amber-600' : highlight ? 'text-emerald-600' : 'text-slate-900'
      }`}>{value}</p>
    </div>
  );
}